import { useMemo } from 'react'
import {
  formatTimeString,
  getCurrentTimeInTimezone,
  addMinutesToTime,
  formatTimeDisplay,
} from '../helpers'

interface IUseTimeComputationsProps {
  selectedDate: Date | null
  startTime: string
  endTime: string
  selectedTimezone: string
  deviceTimezone: string
}

export const useTimeComputations = ({
  selectedDate,
  startTime,
  endTime,
  selectedTimezone,
  deviceTimezone,
}: IUseTimeComputationsProps) => {
  // Get current time rounded to next 15-minute interval in selected timezone
  const getCurrentTimeSlot = useMemo(() => {
    const { hour, minute } = getCurrentTimeInTimezone(selectedTimezone)
    return formatTimeString(hour, minute)
  }, [selectedTimezone])

  // Get default start time based on current time and selected date
  const getDefaultStartTime = useMemo(() => {
    if (!selectedDate) {
      return getCurrentTimeSlot
    }

    const today = new Date()
    const isToday = selectedDate.toDateString() === today.toDateString()

    // If different timezone, default to 9:00 AM to avoid confusion
    if (selectedTimezone !== deviceTimezone) {
      return '09:00'
    }

    if (isToday) {
      return getCurrentTimeSlot
    } else {
      // For future dates, default to 9:00 AM
      return '09:00'
    }
  }, [selectedDate, selectedTimezone, deviceTimezone, getCurrentTimeSlot])

  // Get default end time (1 hour after start time)
  const getDefaultEndTime = useMemo(() => {
    return addMinutesToTime(getDefaultStartTime, 60)
  }, [getDefaultStartTime])

  // Memoize displayed time values
  // Return empty string when no time is selected to show placeholder
  const displayedStartTime = useMemo(() => {
    return startTime ? formatTimeDisplay(startTime) : ''
  }, [startTime])

  const displayedEndTime = useMemo(() => {
    return endTime ? formatTimeDisplay(endTime) : ''
  }, [endTime])

  return {
    getCurrentTimeSlot,
    getDefaultStartTime,
    getDefaultEndTime,
    displayedStartTime,
    displayedEndTime,
  }
}
